import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import * as fromUserActions from '../actions/users.action';

import { Observable, of, firstValueFrom, combineLatest } from 'rxjs';
import { map, catchError, switchMap, take, filter } from 'rxjs/operators';
import { FirebaseService } from '../../services/firebase/firebase.service';
import {
  AngularFirestore,
  DocumentReference,
  QueryFn,
  Query
} from '@angular/fire/compat/firestore';
import { User } from 'src/app/models/user.model';
import { Team, TeamFirebaseResponse } from 'src/app/models/team.models';
import { Payment } from 'src/app/models/payments.model';
import { Doubt } from 'src/app/models/doubt.model';
import { Review } from 'src/app/models/reviews.model';
import { selectTeamById } from '../selectors';
import { Router } from '@angular/router';

@Injectable()
export class UserEffects {
  constructor(
    private readonly action$: Actions,
    private readonly store: Store,
    private readonly firebaseService: FirebaseService,
    private readonly firestore: AngularFirestore,
    private readonly router: Router
  ) {}

  // Add User
  addUser$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromUserActions.AddUserAction),
      map((action) => ({
        type: fromUserActions.ADD_USER_SUCCESS,
        data: action.data
      })),
      catchError((error) =>
        of({ type: fromUserActions.ADD_USER_FAIL, error: error })
      )
    )
  );

  // Delete User
  deleteUser$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromUserActions.DeleteUserAction),
      map(() => ({ type: fromUserActions.DELETE_USER_SUCCESS })),
      catchError((error) =>
        of({ type: fromUserActions.DELETE_USER_FAIL, error: error })
      )
    )
  );

  // Update User
  updateUser$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromUserActions.UpdateUserAction),
      switchMap(async ({ data }) => {
        await this.firebaseService.updateDoc('users', data.id!, data);
        return data;
      }),
      map((data) => ({ type: fromUserActions.UPDATE_USER_SUCCESS, data })),
      catchError((error) =>
        of({ type: fromUserActions.UPDATE_USER_FAIL, error: error })
      )
    )
  );

  // Fetch Clients By Trainer
  fetchClientsByTrainer$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromUserActions.AddUserSuccessAction),
      switchMap(async ({ data }) => {
        const trainerRef = this.firestore.doc(`users/${data.id}`).ref;

        return await firstValueFrom(
          this.firebaseService
            .getDataOfCollectionByAnyField(
              'users',
              'trainerRef',
              '==',
              trainerRef
            )
            .pipe(
              map((value) =>
                value.docs
                  .map((doc) => ({ ...(doc.data() as User), id: doc.id }))
                  .map((user) => {
                    delete user.trainerRef;
                    delete user.teamRef;
                    return user;
                  })
              )
            )
        );
      }),
      map((data) => ({
        type: fromUserActions.FETCH_CLIENTS_BY_TRAINER_SUCCESS,
        data
      })),
      catchError((error) =>
        of({
          type: fromUserActions.FETCH_CLIENTS_BY_TRAINER_FAIL,
          error: error
        })
      )
    )
  );

  // Fetch Teams By Trainer
  fetchTeamsByTrainer$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromUserActions.AddUserSuccessAction),
      switchMap(({ data }) => {
        const trainerRef = this.firestore.doc(`users/${data.id}`).ref;

        return this.getClientsByTrainer(trainerRef).pipe(
          switchMap((clientsByTrainer) =>
            this.addClientsToTeams(clientsByTrainer, trainerRef)
          ),
          map((teams) => ({
            type: fromUserActions.FETCH_TEAMS_BY_TRAINER_SUCCESS,
            data: teams
          })),
          catchError((error) =>
            of({
              type: fromUserActions.FETCH_TEAMS_BY_TRAINER_FAIL,
              error: error
            })
          )
        );
      })
    )
  );

  // Select Team
  selectTeam$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromUserActions.SelectTeamAction),
      switchMap(({ teamId }) => {
        return this.store.select(selectTeamById(teamId)).pipe(
          filter((team) => !!team),
          take(1)
        );
      }),
      switchMap((team) => {
        const userIds = team.members.map((member) => member.id!);
        return combineLatest([
          of(team),
          userIds.length
            ? this.getPendingData('payments', userIds, team.createdBy)
            : of([]),
          userIds.length
            ? this.getPendingData('doubts', userIds, team.createdBy)
            : of([]),
          userIds.length
            ? this.getPendingData('reviews', userIds, team.createdBy)
            : of([])
        ]);
      }),
      switchMap((data) => {
        const membersWithData = this.getMembersWithData(
          data[0],
          data[1] as Payment[],
          data[2] as Doubt[],
          data[3] as Review[]
        );
        return of({ ...data[0], members: membersWithData });
      }),
      map((team) => ({
        type: fromUserActions.SELECT_TEAM_SUCCESS,
        data: team
      })),
      catchError((error) =>
        of({
          type: fromUserActions.SELECT_TEAM_FAIL,
          error: error
        })
      )
    )
  );

  addTeam$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromUserActions.AddTeamAction),
      switchMap(async ({ team }) => {
        const trainerRef = this.firestore.doc(`users/${team.createdBy}`).ref;
        const members = team.members.map(
          (member) => this.firestore.doc(`users/${member.id}`).ref
        );
        const newTeam: TeamFirebaseResponse = {
          ...team,
          members: members,
          createdBy: trainerRef
        };
        const result = await this.firebaseService.addDoc('teams', newTeam);
        this.router.navigate(['/teams']);
        return { ...team, id: result.id };
      }),
      map((team) => ({
        type: fromUserActions.ADD_TEAM_SUCCESS,
        data: team
      })),
      catchError((error) =>
        of({
          type: fromUserActions.ADD_TEAM_FAIL,
          error: error
        })
      )
    )
  );

  updateTeam$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromUserActions.UpdateTeamAction),
      switchMap(async ({ team }) => {
        const trainerRef = this.firestore.doc(`users/${team.createdBy}`).ref;
        const members = team.members.map(
          (member) => this.firestore.doc(`users/${member.id}`).ref
        );
        const newTeam: TeamFirebaseResponse = {
          ...team,
          members: members,
          createdBy: trainerRef
        };
        await this.firebaseService.updateDoc('teams', team.id!, newTeam);
        this.router.navigate(['/teams']);
        return { ...team, id: team.id };
      }),
      map((team) => ({
        type: fromUserActions.UPDATE_TEAM_SUCCESS,
        data: team
      })),
      catchError((error) =>
        of({
          type: fromUserActions.UPDATE_TEAM_FAIL,
          error: error
        })
      )
    )
  );

  deleteTeam$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromUserActions.DeleteTeamAction),
      switchMap(async ({ id }) => {
        await this.firebaseService.deleteDoc('teams', id);
        return id;
      }),
      map((id) => ({
        type: fromUserActions.DELETE_TEAM_SUCCESS,
        id
      })),
      catchError((error) =>
        of({
          type: fromUserActions.DELETE_TEAM_FAIL,
          error: error
        })
      )
    )
  );

  changeTeam$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromUserActions.ChangeTeamAction),
      switchMap(async ({ userId, oldTeam, newTeam }) => {
        const userRef = this.firestore.doc(`users/${userId}`).ref;
        let oldTeamToSave = this.getTeamFirebase(oldTeam);
        oldTeamToSave = {
          ...oldTeamToSave,
          members: oldTeamToSave.members.filter(
            (member) => member.id !== userRef.id
          )
        };
        await this.firebaseService.updateDoc(
          'teams',
          oldTeamToSave.id!,
          oldTeamToSave
        );

        let newTeamToSave = this.getTeamFirebase(newTeam);
        newTeamToSave.members.push(userRef);
        await this.firebaseService.updateDoc(
          'teams',
          newTeamToSave.id!,
          newTeamToSave
        );

        const teamRef = this.firestore.doc(`teams/${newTeamToSave.id}`).ref;
        await this.firebaseService.updateDoc('users', userId, { teamRef });

        return { userId, oldTeam, newTeam };
      }),
      map(({ userId, oldTeam, newTeam }) => ({
        type: fromUserActions.CHANGE_TEAM_SUCCESS,
        userId,
        oldTeam,
        newTeam
      })),
      catchError((error) =>
        of({
          type: fromUserActions.CHANGE_TEAM_FAIL,
          error: error
        })
      )
    )
  );

  unsubscribeUser$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromUserActions.UnsubscribeUserAction),
      switchMap(async ({ userId, oldTeam }) => {
        const userRef = this.firestore.doc(`users/${userId}`).ref;
        let oldTeamToSave = this.getTeamFirebase(oldTeam);
        oldTeamToSave = {
          ...oldTeamToSave,
          members: oldTeamToSave.members.filter(
            (member) => member.id !== userRef.id
          )
        };
        await this.firebaseService.updateDoc(
          'teams',
          oldTeamToSave.id!,
          oldTeamToSave
        );

        await this.firebaseService.updateDoc('users', userId, {
          teamRef: null,
          trainerRef: null
        });

        return { userId, oldTeam };
      }),
      map(({ userId, oldTeam }) => ({
        type: fromUserActions.UNSUBSCRIBE_TEAM_SUCCESS,
        userId,
        oldTeam
      })),
      catchError((error) =>
        of({
          type: fromUserActions.UNSUBSCRIBE_TEAM_FAIL,
          error: error
        })
      )
    )
  );

  private getClientsByTrainer(
    trainerRef: DocumentReference<unknown>
  ): Observable<User[]> {
    return this.firebaseService
      .getDataOfCollectionByAnyField('users', 'trainerRef', '==', trainerRef)
      .pipe(
        map((value) =>
          value.docs
            .map((doc) => ({ ...(doc.data() as User), id: doc.id }))
            .map((user) => {
              delete user.trainerRef;
              delete user.teamRef;
              return user;
            })
        )
      );
  }

  private addClientsToTeams(
    clientsByTrainer: User[],
    trainerRef: DocumentReference<unknown>
  ): Observable<Team[]> {
    return this.firebaseService
      .getDataOfCollectionByAnyField('teams', 'createdBy', '==', trainerRef)
      .pipe(
        map((value) =>
          value.docs
            .map((doc) => ({
              ...(doc.data() as TeamFirebaseResponse),
              id: doc.id
            }))
            .map((team) => {
              const members = clientsByTrainer.filter((client) =>
                team.members.find((memberRef) => memberRef.id === client.id)
              );
              const newTeam: Team = {
                ...team,
                createdBy: team.createdBy.id,
                members
              };
              return newTeam;
            })
        )
      );
  }

  private getPendingData(
    collectionName: string,
    userIds: string[],
    trainerId: string
  ): Observable<Payment[] | Doubt[] | Review[]> {
    const queryFn: QueryFn = (ref) => {
      let query: Query = ref;
      query = query.where('state', '==', 'PENDING');

      let userRefs = userIds.map(
        (userId) => this.firestore.doc(`users/${userId}`).ref
      );
      query = query.where('userRef', 'in', userRefs);

      const trainerRef = this.firestore.doc(`users/${trainerId}`).ref;
      query = query.where('trainerRef', '==', trainerRef);

      return query;
    };

    return this.firebaseService
      .getDataOfCollectionByMultipleFields(collectionName, queryFn)
      .pipe(
        take(1),
        map((resp) =>
          resp.docs.map((doc) => ({
            ...(doc.data() as any),
            id: doc.id
          }))
        )
      );
  }

  private getTeamFirebase(team: Team): TeamFirebaseResponse {
    const trainerRef = this.firestore.doc(`users/${team.createdBy}`).ref;
    const members = team.members.map(
      (member) => this.firestore.doc(`users/${member.id}`).ref
    );
    return {
      ...team,
      members: members,
      createdBy: trainerRef
    };
  }

  private getMembersWithData(
    team: Team,
    payments: Payment[],
    doubts: Doubt[],
    reviews: Review[]
  ): User[] {
    return team.members.map((member) => {
      const userReviews = reviews.filter(
        (review) =>
          review.userRef!.id === member.id && review.reviewDate !== null
      );
      const lastReview =
        userReviews.length > 0
          ? userReviews.reduce((closest, current) => {
              const currentReviewDate = new Date(current.reviewDate!);
              const closestReviewDate = new Date(closest.reviewDate!);

              return Math.abs(currentReviewDate.getTime() - Date.now()) <
                Math.abs(closestReviewDate.getTime() - Date.now())
                ? current
                : closest;
            })
          : null;

      return {
        ...member,
        pendingPaymentsCount: payments.filter(
          (payment) => payment.userRef!.id === member.id
        ).length,
        pendingDoubtsCount: doubts.filter(
          (doubt) => doubt.userRef!.id === member.id
        ).length,
        daysUntilReview: lastReview
          ? Math.floor(
              ((Date.now() - new Date(lastReview.reviewDate as any).getTime()) /
                (1000 * 60 * 60 * 24)) *
                -1
            )
          : -1
      };
    });
  }
}
